export const DRIVER_CAMERA_STAMP_VERSION = "sedulur-dalan-camera-stamp-v1.6.1";
export const DRIVER_CAMERA_GEOCODER = "LOCATIONIQ_SERVER_PROXY";
export const DRIVER_CAMERA_MAP_FALLBACK_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
export const DRIVER_CAMERA_MAP_FALLBACK_ATTRIBUTION = "© OpenStreetMap contributors";

const JAKARTA_TIME_ZONE = "Asia/Jakarta";
const DRIVER_EVIDENCE_FONT_STACK = '"Roboto", "Noto Sans", "Segoe UI Variable Text", "Segoe UI", Arial, sans-serif';
const GEOCODER_API_URL = "/api/map/reverse-geocode";
const MAP_WIDTH = 480;
const MAP_HEIGHT = 360;
const MAP_ZOOM = 15;
const NETWORK_TIMEOUT_MS = 8_000;
const GEOCODER_CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const GEOCODER_CACHE_COORDINATE_DECIMALS = 5;
const geocoderMemoryCache = new Map();

const DISALLOWED_ADDRESS_PARTS = [
  /asia\s*\/\s*jakarta/i,
  /kepulauan\s+sunda\s+besar/i,
  /kepulauan\s+melayu/i,
  /^indonesia$/i,
];

async function fetchWithCaptureTimeout(url, options = {}, timeoutMs = NETWORK_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
}

function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function cleanText(value, maxLength = 240) {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  return text ? text.slice(0, maxLength) : "";
}

function uniqueParts(parts) {
  const seen = new Set();
  return parts.filter((part) => {
    const value = cleanText(part, 120);
    const key = value.toLocaleLowerCase("id-ID");
    if (!value || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function normalizeDriverAddressText(value) {
  const rawSegments = cleanText(value, 640)
    .split(",")
    .map((part) => cleanText(part, 160))
    .filter(Boolean)
    .filter((part) => !DISALLOWED_ADDRESS_PARTS.some((pattern) => pattern.test(part)));
  const unique = uniqueParts(rawSegments);
  const compact = unique.filter((part, index, all) => {
    if (/^\d{5}$/.test(part)) return true;
    const normalized = part.toLocaleLowerCase("id-ID");
    return !all.some((other, otherIndex) => {
      if (otherIndex === index) return false;
      const otherNormalized = other.toLocaleLowerCase("id-ID");
      return otherNormalized.length > normalized.length
        && otherNormalized.includes(normalized)
        && /[a-z]/i.test(normalized);
    });
  });
  const merged = [];
  for (const part of compact) {
    if (/^\d{1,4}[a-z]?$/i.test(part) && merged.length > 0 && !/^\d{5}$/.test(part)) {
      merged[merged.length - 1] = `${merged[merged.length - 1]} No. ${part}`;
      continue;
    }
    merged.push(part);
  }
  return merged.slice(0, 9).join(", ") || "Alamat tidak tersedia";
}

function geocoderCacheKey(latitude, longitude) {
  const lat = finiteNumber(latitude);
  const lon = finiteNumber(longitude);
  if (lat === null || lon === null) return null;
  return `${lat.toFixed(GEOCODER_CACHE_COORDINATE_DECIMALS)},${lon.toFixed(GEOCODER_CACHE_COORDINATE_DECIMALS)}`;
}

function readGeocoderMemoryCache(key) {
  if (!key) return null;
  const cached = geocoderMemoryCache.get(key);
  if (!cached || cached.expiresAt <= Date.now()) {
    geocoderMemoryCache.delete(key);
    return null;
  }
  return cached.value;
}

function writeGeocoderMemoryCache(key, value) {
  if (!key || !value?.addressText || value.addressText === "Alamat tidak tersedia") return;
  if (geocoderMemoryCache.size >= 64) {
    const oldestKey = geocoderMemoryCache.keys().next().value;
    if (oldestKey) geocoderMemoryCache.delete(oldestKey);
  }
  geocoderMemoryCache.set(key, { value, expiresAt: Date.now() + GEOCODER_CACHE_TTL_MS });
}

export function resolveDriverCaptureTime(file, sourceType = "CAMERA", nowMs = Date.now()) {
  const source = String(sourceType || "CAMERA").toUpperCase();
  if (source !== "CAMERA") return new Date(nowMs).toISOString();
  const lastModified = Number(file?.lastModified || 0);
  const plausible = Number.isFinite(lastModified)
    && lastModified > 0
    && Math.abs(nowMs - lastModified) <= 30 * 60 * 1000;
  return new Date(plausible ? lastModified : nowMs).toISOString();
}

export function formatDriverStampTime(value) {
  return new Intl.DateTimeFormat("id-ID", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: JAKARTA_TIME_ZONE,
  }).format(new Date(value)).replaceAll(".", ":");
}

export function formatDriverStampDate(value) {
  return new Intl.DateTimeFormat("id-ID", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: JAKARTA_TIME_ZONE,
  }).format(new Date(value));
}

export function formatDriverCoordinates(location = {}) {
  const latitude = finiteNumber(location.latitude);
  const longitude = finiteNumber(location.longitude);
  if (latitude === null || longitude === null) return "Koordinat tidak tersedia";
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
}

export function formatDriverGpsAccuracy(location = {}) {
  const accuracy = finiteNumber(location.accuracyM);
  if (accuracy === null || accuracy < 0) return "Akurasi GPS tidak tersedia";
  return `±${Math.max(1, Math.round(accuracy))} m`;
}

export function formatDriverLocationAddress(payload = {}) {
  const parts = uniqueParts([
    payload.locality,
    payload.city,
    payload.principalSubdivision,
    payload.postcode,
  ]);
  return normalizeDriverAddressText(parts.join(", "));
}

export function buildDriverStampSummary({
  capturedAt,
  addressText,
  location = {},
  driverName,
  activityLabel,
  fleetOrderNumber,
  stopName,
  stopSequence,
  stopTotal,
  vehiclePlate,
  sourceType = "CAMERA",
} = {}) {
  const stopIndex = Number(stopSequence);
  const stopCount = Number(stopTotal);
  const stopPrefix = Number.isInteger(stopIndex) && stopIndex > 0
    ? `Stop ${String(stopIndex).padStart(2, "0")}${Number.isInteger(stopCount) && stopCount > 0 ? `/${String(stopCount).padStart(2, "0")}` : ""}`
    : "";
  return {
    time: formatDriverStampTime(capturedAt || new Date().toISOString()),
    date: formatDriverStampDate(capturedAt || new Date().toISOString()),
    address: normalizeDriverAddressText(addressText),
    driver: cleanText(driverName, 100) || "Driver tidak tersedia",
    activity: cleanText(activityLabel, 120) || "Bukti operasional",
    fleetOrder: cleanText(fleetOrderNumber, 80),
    stop: [stopPrefix, cleanText(stopName, 140)].filter(Boolean).join(" · "),
    vehicle: cleanText(vehiclePlate, 60),
    coordinates: formatDriverCoordinates(location),
    accuracy: formatDriverGpsAccuracy(location),
    source: String(sourceType || "CAMERA").toUpperCase() === "GALLERY" ? "Galeri" : "Kamera",
  };
}

export async function reverseGeocodeDriverLocation(location = {}) {
  const latitude = finiteNumber(location.latitude);
  const longitude = finiteNumber(location.longitude);
  if (latitude === null || longitude === null) {
    return { addressText: "Alamat tidak tersedia", status: "LOCATION_UNAVAILABLE", provider: null };
  }
  const cacheKey = geocoderCacheKey(latitude, longitude);
  const cached = readGeocoderMemoryCache(cacheKey);
  if (cached) {
    return {
      ...cached,
      status: typeof navigator !== "undefined" && navigator.onLine === false
        ? "CACHED_OFFLINE"
        : cached.status,
    };
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { addressText: "Alamat tidak tersedia", status: "OFFLINE", provider: DRIVER_CAMERA_GEOCODER };
  }
  try {
    const url = new URL(GEOCODER_API_URL, window.location.origin);
    url.searchParams.set("latitude", String(latitude));
    url.searchParams.set("longitude", String(longitude));
    const response = await fetchWithCaptureTimeout(url.toString(), {
      method: "GET",
      credentials: "same-origin",
      cache: "default",
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    const result = {
      addressText: normalizeDriverAddressText(payload.addressText || formatDriverLocationAddress(payload)),
      status: payload.status || "RESOLVED",
      provider: payload.provider || DRIVER_CAMERA_GEOCODER,
      locality: cleanText(payload.locality, 120) || null,
      city: cleanText(payload.city, 120) || null,
      region: cleanText(payload.region || payload.principalSubdivision, 120) || null,
      postcode: cleanText(payload.postcode, 40) || null,
    };
    writeGeocoderMemoryCache(cacheKey, result);
    return result;
  } catch {
    return { addressText: "Alamat tidak tersedia", status: "FAILED", provider: DRIVER_CAMERA_GEOCODER };
  }
}

async function loadDriverMapConfig() {
  try {
    const response = await fetchWithCaptureTimeout("/api/map/config", {
      credentials: "same-origin",
      cache: "default",
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    return {
      attribution: cleanText(payload.attribution, 160) || DRIVER_CAMERA_MAP_FALLBACK_ATTRIBUTION,
    };
  } catch {
    return { attribution: DRIVER_CAMERA_MAP_FALLBACK_ATTRIBUTION };
  }
}

function tilePoint(latitude, longitude, zoom) {
  const lat = Math.max(-85.05112878, Math.min(85.05112878, latitude));
  const n = 2 ** zoom;
  const x = ((longitude + 180) / 360) * n;
  const latRad = lat * Math.PI / 180;
  const y = (1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2 * n;
  return { x, y };
}

function tileUrl(zoom, x, y) {
  return `/api/map/tile/${zoom}/${x}/${y}.png`;
}

function decodeBlobImage(blob) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Tile map tidak dapat dibaca."));
    };
    image.src = objectUrl;
  });
}

async function decodeTile(url) {
  const response = await fetchWithCaptureTimeout(url, {
    method: "GET",
    credentials: "same-origin",
    cache: "default",
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  if (!String(blob.type || "").toLowerCase().startsWith("image/")) {
    throw new Error("Respons tile bukan gambar.");
  }
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(blob);
    } catch {
      // Android/WebView builds can expose createImageBitmap but still fail on
      // particular image payloads. Fall through to the DOM image decoder.
    }
  }
  return decodeBlobImage(blob);
}

function roundRectPath(context, x, y, width, height, radius) {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + width, y, x + width, y + height, r);
  context.arcTo(x + width, y + height, x, y + height, r);
  context.arcTo(x, y + height, x, y, r);
  context.arcTo(x, y, x + width, y, r);
  context.closePath();
}

function drawMapFallback(context, width, height, label = "Peta tidak tersedia") {
  context.fillStyle = "#14393b";
  context.fillRect(0, 0, width, height);
  context.strokeStyle = "rgba(255,255,255,0.14)";
  context.lineWidth = 1;
  const spacing = Math.max(24, Math.round(width / 8));
  for (let x = -height; x < width + height; x += spacing) {
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x + height, height);
    context.stroke();
  }
  context.fillStyle = "rgba(255,255,255,0.9)";
  context.font = `700 ${Math.max(14, Math.round(width * 0.045))}px ${DRIVER_EVIDENCE_FONT_STACK}`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(label, width / 2, height / 2);
}

function drawMapPin(context, x, y, size = 17) {
  context.save();
  context.shadowColor = "rgba(0,0,0,0.32)";
  context.shadowBlur = 8;
  context.fillStyle = "#f2a33a";
  context.beginPath();
  context.arc(x, y - size * 0.45, size * 0.55, 0, Math.PI * 2);
  context.fill();
  context.beginPath();
  context.moveTo(x - size * 0.35, y - size * 0.15);
  context.lineTo(x, y + size * 0.7);
  context.lineTo(x + size * 0.35, y - size * 0.15);
  context.closePath();
  context.fill();
  context.fillStyle = "#ffffff";
  context.beginPath();
  context.arc(x, y - size * 0.45, size * 0.17, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

export async function renderDriverMiniMap(location = {}) {
  const latitude = finiteNumber(location.latitude);
  const longitude = finiteNumber(location.longitude);
  const canvas = document.createElement("canvas");
  canvas.width = MAP_WIDTH;
  canvas.height = MAP_HEIGHT;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) return { canvas: null, status: "FAILED", attribution: DRIVER_CAMERA_MAP_FALLBACK_ATTRIBUTION };
  if (latitude === null || longitude === null) {
    drawMapFallback(context, canvas.width, canvas.height, "GPS tidak tersedia");
    return { canvas, status: "LOCATION_UNAVAILABLE", attribution: DRIVER_CAMERA_MAP_FALLBACK_ATTRIBUTION };
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    drawMapFallback(context, canvas.width, canvas.height, "Peta belum tersedia (offline)");
    return { canvas, status: "OFFLINE", attribution: DRIVER_CAMERA_MAP_FALLBACK_ATTRIBUTION };
  }

  const config = await loadDriverMapConfig();
  const center = tilePoint(latitude, longitude, MAP_ZOOM);
  const worldX = center.x * 256;
  const worldY = center.y * 256;
  const left = worldX - canvas.width / 2;
  const top = worldY - canvas.height / 2;
  const minTileX = Math.floor(left / 256);
  const maxTileX = Math.floor((left + canvas.width - 1) / 256);
  const minTileY = Math.floor(top / 256);
  const maxTileY = Math.floor((top + canvas.height - 1) / 256);
  const tileCount = 2 ** MAP_ZOOM;
  let drawn = 0;

  context.fillStyle = "#dbe7e3";
  context.fillRect(0, 0, canvas.width, canvas.height);
  const jobs = [];
  for (let ty = minTileY; ty <= maxTileY; ty += 1) {
    for (let tx = minTileX; tx <= maxTileX; tx += 1) {
      if (ty < 0 || ty >= tileCount) continue;
      const wrappedX = ((tx % tileCount) + tileCount) % tileCount;
      const x = tx * 256 - left;
      const y = ty * 256 - top;
      jobs.push(
        decodeTile(tileUrl(MAP_ZOOM, wrappedX, ty))
          .then((image) => {
            context.drawImage(image, x, y, 256, 256);
            image.close?.();
            drawn += 1;
          })
          .catch(() => {}),
      );
    }
  }
  await Promise.all(jobs);
  if (!drawn) {
    drawMapFallback(context, canvas.width, canvas.height);
    return { canvas, status: "FAILED", attribution: config.attribution };
  }
  drawMapPin(context, canvas.width / 2, canvas.height / 2, 25);
  context.fillStyle = "rgba(12, 34, 35, 0.78)";
  context.fillRect(0, canvas.height - 24, canvas.width, 24);
  context.fillStyle = "#ffffff";
  context.font = `600 11px ${DRIVER_EVIDENCE_FONT_STACK}`;
  context.textAlign = "right";
  context.textBaseline = "middle";
  context.fillText(config.attribution, canvas.width - 8, canvas.height - 12);
  return { canvas, status: "RESOLVED", attribution: config.attribution };
}

function decodeImageWithDomElement(file) {
  return new Promise((resolve, reject) => {
    const sourceUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => resolve({
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      close: () => URL.revokeObjectURL(sourceUrl),
    });
    image.onerror = () => {
      URL.revokeObjectURL(sourceUrl);
      reject(new Error("Foto tidak dapat dibaca untuk membuat timestamp."));
    };
    image.src = sourceUrl;
  });
}

async function decodeImage(file) {
  // Do not pass imageOrientation explicitly. Some Android System WebView builds
  // expose createImageBitmap() but reject the standards-compliant `from-image`
  // enum before decoding. Omitting the option keeps the browser default EXIF
  // behavior while widening compatibility.
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        close: () => bitmap.close?.(),
      };
    } catch {
      // A callable createImageBitmap() is not proof that the current WebView can
      // decode the camera payload. Fall through to HTMLImageElement + canvas.
    }
  }

  return decodeImageWithDomElement(file);
}

function wrapText(context, text, maxWidth, maxLines = 2) {
  const words = cleanText(text, 400).split(" ").filter(Boolean);
  const lines = [];
  let current = "";
  let wordIndex = 0;

  while (wordIndex < words.length && lines.length < maxLines) {
    const word = words[wordIndex];
    const candidate = current ? `${current} ${word}` : word;
    if (!current || context.measureText(candidate).width <= maxWidth) {
      current = candidate;
      wordIndex += 1;
      continue;
    }

    lines.push(current);
    current = "";
  }

  if (current && lines.length < maxLines) lines.push(current);
  const hasOverflow = wordIndex < words.length;
  if (hasOverflow && lines.length > 0) {
    const lastIndex = lines.length - 1;
    let lastLine = lines[lastIndex];
    while (lastLine.length > 1 && context.measureText(`${lastLine}…`).width > maxWidth) {
      lastLine = lastLine.slice(0, -1).trimEnd();
    }
    lines[lastIndex] = `${lastLine}…`;
  }

  return lines.length ? lines : ["Alamat tidak tersedia"];
}

function fitTextToWidth(context, value, maxWidth) {
  let text = cleanText(value, 260);
  if (context.measureText(text).width <= maxWidth) return text;
  while (text.length > 1 && context.measureText(`${text}…`).width > maxWidth) {
    text = text.slice(0, -1).trimEnd();
  }
  return text ? `${text}…` : "…";
}

function drawMetaLine(context, label, value, x, y, maxWidth, fontSize, muted = false) {
  if (!value) return y;
  const safeLabel = cleanText(label, 80);
  const safeValue = cleanText(value, 240);
  const labelGap = safeLabel ? Math.max(4, Math.round(fontSize * 0.24)) : 0;
  context.fillStyle = muted ? "rgba(255,255,255,0.76)" : "rgba(255,255,255,0.96)";

  let valueX = x;
  let remainingWidth = maxWidth;
  if (safeLabel) {
    const prefix = `${safeLabel}:`;
    context.font = `600 ${fontSize}px ${DRIVER_EVIDENCE_FONT_STACK}`;
    const fittedPrefix = fitTextToWidth(context, prefix, maxWidth);
    context.fillText(fittedPrefix, x, y);
    const prefixWidth = Math.min(maxWidth, context.measureText(fittedPrefix).width);
    valueX += prefixWidth + labelGap;
    remainingWidth = Math.max(Math.round(fontSize * 2.5), maxWidth - prefixWidth - labelGap);
  }

  context.font = `400 ${fontSize}px ${DRIVER_EVIDENCE_FONT_STACK}`;
  const fittedValue = fitTextToWidth(context, safeValue, remainingWidth);
  context.fillText(fittedValue, valueX, y);
  return y + Math.round(fontSize * 1.22);
}

export function calculateDriverEvidenceLayout({
  width,
  height,
  mapStatus = "RESOLVED",
  addressLineCount = 3,
  metaRowCount = 4,
} = {}) {
  const safeWidth = Math.max(320, Number(width) || 320);
  const safeHeight = Math.max(320, Number(height) || 320);
  const reference = Math.min(safeWidth, safeHeight * 0.90);
  const margin = Math.max(16, Math.round(safeWidth * 0.020));
  const gap = Math.max(20, Math.round(reference * 0.025));
  const radius = Math.max(12, Math.round(reference * 0.014));
  const padding = Math.max(18, Math.round(reference * 0.023));
  const panelWidth = safeWidth - margin * 2;
  const panelHeight = Math.round(safeHeight * 0.25);
  const mapResolved = mapStatus === "RESOLVED";
  const mapHasCanvas = Boolean(mapStatus);
  const landscape = safeWidth > safeHeight * 1.15;
  const metaColumns = landscape ? 2 : 1;
  const metaVisualRows = Math.ceil(Math.max(0, metaRowCount) / metaColumns);
  const mapRatio = mapResolved ? 0.40 : 0.28;
  const mapWidth = mapHasCanvas
    ? Math.min(Math.round(panelWidth * mapRatio), Math.round(safeWidth * 0.42))
    : 0;
  const mapHeight = mapHasCanvas
    ? Math.min(panelHeight - padding * 2, Math.round(mapWidth * (MAP_HEIGHT / MAP_WIDTH)))
    : 0;
  const infoWidth = panelWidth - padding * 2 - mapWidth - (mapHasCanvas ? gap : 0);

  const base = {
    timeFont: Math.max(46, Math.round(reference * 0.071)),
    dateFont: Math.max(22, Math.round(reference * 0.035)),
    bodyFont: Math.max(21, Math.round(reference * 0.0315)),
    smallFont: Math.max(19, Math.round(reference * 0.0275)),
  };
  const availableTextHeight = panelHeight - padding * 2;
  const baseContentHeight = Math.round(base.timeFont * 1.04)
    + Math.max(1, addressLineCount) * Math.round(base.bodyFont * 1.18)
    + Math.round(gap * 0.24)
    + metaVisualRows * Math.round(base.smallFont * 1.22);
  const fitScale = Math.min(1, availableTextHeight / Math.max(1, baseContentHeight));

  return {
    width: safeWidth,
    height: safeHeight,
    reference,
    margin,
    gap,
    radius,
    padding,
    panelWidth,
    panelHeight,
    mapRatio,
    mapWidth,
    mapHeight,
    infoWidth,
    metaColumns,
    metaVisualRows,
    timeFont: Math.max(42, Math.round(base.timeFont * fitScale)),
    dateFont: Math.max(20, Math.round(base.dateFont * fitScale)),
    bodyFont: Math.max(19, Math.round(base.bodyFont * fitScale)),
    smallFont: Math.max(17, Math.round(base.smallFont * fitScale)),
  };
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

async function encodeStampedCanvas(canvas, maxBytes = 2 * 1024 * 1024) {
  let working = canvas;
  let blob = null;
  for (let sizeAttempt = 0; sizeAttempt < 3; sizeAttempt += 1) {
    for (const quality of [0.88, 0.82, 0.76, 0.68, 0.58]) {
      blob = await canvasToBlob(working, quality);
      if (blob && blob.size <= maxBytes) return { blob, width: working.width, height: working.height };
    }
    if (sizeAttempt < 2) {
      const reduced = document.createElement("canvas");
      reduced.width = Math.max(1, Math.round(working.width * 0.84));
      reduced.height = Math.max(1, Math.round(working.height * 0.84));
      const reducedContext = reduced.getContext("2d", { alpha: false });
      if (!reducedContext) break;
      reducedContext.drawImage(working, 0, 0, reduced.width, reduced.height);
      working = reduced;
    }
  }
  throw new Error("Foto bertimestamp masih terlalu besar untuk disimpan.");
}

export async function stampDriverEvidence(file, stampContext, miniMap = null) {
  const decoded = await decodeImage(file);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = decoded.width;
    canvas.height = decoded.height;
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("Perangkat tidak dapat membuat timestamp foto.");
    context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height);

    const width = canvas.width;
    const height = canvas.height;
    const mapResolved = miniMap?.status === "RESOLVED" && Boolean(miniMap?.canvas);
    const mapHasCanvas = Boolean(miniMap?.canvas);
    const summary = buildDriverStampSummary(stampContext);
    const addressMaxLines = width > height * 1.15 ? 2 : 4;
    const initialLayout = calculateDriverEvidenceLayout({
      width,
      height,
      mapStatus: mapHasCanvas ? (mapResolved ? "RESOLVED" : miniMap?.status || "FAILED") : "",
      addressLineCount: addressMaxLines,
      metaRowCount: 5,
    });
    context.font = `500 ${initialLayout.bodyFont}px ${DRIVER_EVIDENCE_FONT_STACK}`;
    const addressLines = wrapText(context, summary.address, initialLayout.infoWidth, addressMaxLines);
    const contextLine = [
      summary.fleetOrder || "",
      summary.vehicle || "",
    ].filter(Boolean).join(" · ");
    const metaRows = [
      { label: "Driver", value: summary.driver },
      {
        label: "GPS",
        value: summary.coordinates === "Koordinat tidak tersedia"
          ? "Tidak tersedia"
          : `${summary.coordinates} · ${summary.accuracy}`,
        muted: true,
      },
      { label: "", value: contextLine },
      { label: "", value: summary.activity },
      { label: "", value: summary.stop },
      { label: "Sumber", value: summary.source === "Galeri" ? summary.source : "", muted: true },
    ].filter((row) => row.value);
    const layout = calculateDriverEvidenceLayout({
      width,
      height,
      mapStatus: mapHasCanvas ? (mapResolved ? "RESOLVED" : miniMap?.status || "FAILED") : "",
      addressLineCount: addressLines.length,
      metaRowCount: metaRows.length,
    });
    const {
      reference,
      margin,
      gap,
      radius,
      padding,
      panelWidth,
      panelHeight,
      mapWidth,
      mapHeight,
      infoWidth,
      metaColumns,
      metaVisualRows,
      timeFont,
      dateFont,
      bodyFont,
      smallFont,
    } = layout;
    const panelY = height - margin - panelHeight;
    const visualTop = panelY;
    const gradient = context.createLinearGradient(0, Math.max(0, visualTop - panelHeight * 0.30), 0, height);
    gradient.addColorStop(0, "rgba(5,27,29,0)");
    gradient.addColorStop(1, "rgba(5,27,29,0.42)");
    context.fillStyle = gradient;
    context.fillRect(0, Math.max(0, visualTop - panelHeight * 0.30), width, height - Math.max(0, visualTop - panelHeight * 0.30));

    roundRectPath(context, margin, panelY, panelWidth, panelHeight, radius);
    context.fillStyle = "rgba(8,37,39,0.88)";
    context.fill();
    context.strokeStyle = "rgba(119,205,200,0.34)";
    context.lineWidth = Math.max(1, Math.round(width * 0.0014));
    context.stroke();

    context.fillStyle = "#42c8bf";
    context.fillRect(margin, panelY + radius, Math.max(5, Math.round(width * 0.005)), Math.max(0, panelHeight - radius * 2));

    const accentWidth = Math.max(4, Math.round(width * 0.0045));
    const mapX = mapHasCanvas ? margin + panelWidth - padding - mapWidth : margin + panelWidth - padding;
    const infoClipX = margin + padding + accentWidth + Math.max(6, Math.round(reference * 0.007));
    const infoClipRight = mapHasCanvas ? mapX - gap : margin + panelWidth - padding;
    const textWidth = Math.max(40, infoClipRight - infoClipX);
    const x = infoClipX;
    const textBlockHeight = Math.round(timeFont * 1.04)
      + addressLines.length * Math.round(bodyFont * 1.18)
      + Math.round(gap * 0.24)
      + metaVisualRows * Math.round(smallFont * 1.22);
    let y = panelY + Math.max(padding, Math.round((panelHeight - textBlockHeight) / 2));

    // Hard, invisible boundary: text is clipped to its own region and can never
    // intrude into the map. The visual separation is provided by the gutter.
    context.save();
    context.beginPath();
    context.rect(
      infoClipX,
      panelY + padding,
      textWidth,
      Math.max(1, panelHeight - padding * 2),
    );
    context.clip();

    context.textAlign = "left";
    context.textBaseline = "top";
    context.fillStyle = "#ffffff";
    context.font = `500 ${timeFont}px ${DRIVER_EVIDENCE_FONT_STACK}`;
    context.fillText(summary.time, x, y);
    const timeWidth = context.measureText(summary.time).width;
    const dateLines = summary.date.split(",").map((part) => part.trim()).filter(Boolean);
    let fittedDateFont = dateFont;
    let dateWidth = Number.POSITIVE_INFINITY;
    const availableDateWidth = Math.max(40, textWidth - timeWidth - gap);
    while (fittedDateFont > 16) {
      context.font = `500 ${fittedDateFont}px ${DRIVER_EVIDENCE_FONT_STACK}`;
      dateWidth = Math.max(...dateLines.map((line) => context.measureText(line).width));
      if (dateWidth <= availableDateWidth) break;
      fittedDateFont -= 1;
    }
    context.fillStyle = "#a8e0dc";
    context.font = `500 ${fittedDateFont}px ${DRIVER_EVIDENCE_FONT_STACK}`;
    const dateLineHeight = Math.round(fittedDateFont * 1.08);
    const dateBlockHeight = dateLineHeight * dateLines.length;
    const dateX = x + textWidth - Math.min(dateWidth, availableDateWidth);
    const dateY = y + Math.max(0, Math.round((timeFont - dateBlockHeight) / 2));
    dateLines.forEach((line, index) => context.fillText(line, dateX, dateY + dateLineHeight * index));
    y += Math.round(timeFont * 1.04);

    context.fillStyle = "rgba(255,255,255,0.96)";
    context.font = `500 ${bodyFont}px ${DRIVER_EVIDENCE_FONT_STACK}`;
    for (const line of addressLines) {
      context.fillText(line, x, y);
      y += Math.round(bodyFont * 1.18);
    }
    y += Math.round(gap * 0.24);

    if (metaColumns === 1) {
      for (const row of metaRows) {
        y = drawMetaLine(context, row.label, row.value, x, y, textWidth, smallFont, row.muted);
      }
    } else {
      const columnGap = Math.max(gap, Math.round(smallFont * 0.9));
      const columnWidth = Math.max(1, Math.floor((textWidth - columnGap) / 2));
      const metaStartY = y;
      metaRows.forEach((row, index) => {
        const rowIndex = Math.floor(index / 2);
        const columnIndex = index % 2;
        const rowX = x + columnIndex * (columnWidth + columnGap);
        const rowY = metaStartY + rowIndex * Math.round(smallFont * 1.22);
        drawMetaLine(context, row.label, row.value, rowX, rowY, columnWidth, smallFont, row.muted);
      });
      y = metaStartY + metaVisualRows * Math.round(smallFont * 1.22);
    }
    context.restore();

    if (mapHasCanvas) {
      const mapY = panelY + Math.round((panelHeight - mapHeight) / 2);
      roundRectPath(context, mapX, mapY, mapWidth, mapHeight, radius);
      context.save();
      context.clip();
      context.drawImage(miniMap.canvas, mapX, mapY, mapWidth, mapHeight);
      context.restore();
      context.strokeStyle = mapResolved ? "rgba(255,255,255,0.62)" : "rgba(255,255,255,0.36)";
      context.lineWidth = Math.max(1, Math.round(width * 0.0015));
      roundRectPath(context, mapX, mapY, mapWidth, mapHeight, radius);
      context.stroke();
    }

    const { blob, width: encodedWidth, height: encodedHeight } = await encodeStampedCanvas(canvas);
    return {
      file: new File([blob], `sedulur-dalan-${Date.now()}.jpg`, { type: "image/jpeg", lastModified: Date.now() }),
      width: encodedWidth,
      height: encodedHeight,
    };
  } finally {
    decoded.close();
  }
}
